import api from "@/lib/api";
import { pickMime } from "@/components/meeting/recorder";

export { pickMime };
const FLUSH_AT = 2 * 1024 * 1024;
const PIECE = 4 * 1024 * 1024;
const q = (token) => (token ? { token } : {});

/* Buffers recorder output and uploads ~2MB pieces in order while recording runs. */
export function chunkSink(eid, token = "") {
  let buf = [], size = 0, seq = 0, queue = Promise.resolve(), failed = 0;
  const flush = () => {
    if (!size) return;
    const blob = new Blob(buf), n = seq++;
    buf = []; size = 0;
    const put = () => api.post(`/api/podcast/episodes/${eid}/chunk`, blob, { params: { seq: n, ...q(token) }, headers: { "Content-Type": "application/octet-stream" } });
    queue = queue.then(() => put().catch(() => put())).catch(() => { failed += 1; });
  };
  return {
    push: (b) => { buf.push(b); size += b.size; if (size >= FLUSH_AT) flush(); },
    finish: async (duration_secs, mime) => { flush(); await queue; if (failed) throw new Error(`${failed} piece(s) failed to upload`); await api.post(`/api/podcast/episodes/${eid}/finish`, { duration_secs, mime }, { params: q(token) }); },
  };
}

export async function uploadFile(eid, file, onProgress) {
  const total = Math.ceil(file.size / PIECE);
  for (let i = 0; i < total; i++) {
    const part = file.slice(i * PIECE, (i + 1) * PIECE);
    const put = () => api.post(`/api/podcast/episodes/${eid}/chunk`, part, { params: { seq: i }, headers: { "Content-Type": "application/octet-stream" } });
    await put().catch(() => put());
    onProgress(Math.round(((i + 1) / total) * 100));
  }
  const dur = await new Promise(res => { const v = document.createElement("video"); v.preload = "metadata"; v.onloadedmetadata = () => res(Math.round(v.duration) || 0); v.onerror = () => res(0); v.src = URL.createObjectURL(file); });
  await api.post(`/api/podcast/episodes/${eid}/finish`, { duration_secs: dur, mime: file.type || "video/mp4" });
}

/* Draws one or two video elements onto a 1280x720 canvas (solo, picture-in-picture or side by side), mixes audio, records. */
export function startComposite({ getVideos, getStreams, getLayout, label, onData }) {
  const W = 1280, H = 720, mime = pickMime();
  const canvas = document.createElement("canvas"); canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  const logo = new Image(); logo.src = "/brand/directory-logo.webp";
  const fit = (v, x, y, w, h, cover) => {
    const r = (cover ? Math.max : Math.min)(w / v.videoWidth, h / v.videoHeight), dw = v.videoWidth * r, dh = v.videoHeight * r;
    ctx.save(); ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip(); ctx.drawImage(v, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh); ctx.restore();
  };
  const draw = () => {
    ctx.fillStyle = "#0b1220"; ctx.fillRect(0, 0, W, H);
    const vids = getVideos().filter(v => v && v.readyState >= 2 && v.videoWidth);
    const layout = getLayout();
    if (vids.length >= 2 && layout === "side") { fit(vids[0], 0, 80, W / 2 - 4, H - 160, true); fit(vids[1], W / 2 + 4, 80, W / 2 - 4, H - 160, true); }
    else if (vids.length >= 2) { fit(vids[1], 0, 0, W, H, true); ctx.fillStyle = "#fff"; ctx.fillRect(W - 372, H - 232, 344, 196); fit(vids[0], W - 370, H - 230, 340, 192, true); }
    else if (vids[0]) fit(vids[0], 0, 0, W, H, true);
    ctx.fillStyle = "rgba(15,23,42,0.82)"; ctx.fillRect(24, H - 96, 560, 64);
    ctx.fillStyle = "#f59e0b"; ctx.fillRect(24, H - 96, 6, 64);
    ctx.fillStyle = "#fff"; ctx.font = "bold 26px sans-serif"; ctx.fillText(label().title.slice(0, 34), 44, H - 60);
    ctx.fillStyle = "#cbd5e1"; ctx.font = "18px sans-serif"; ctx.fillText(label().sub.slice(0, 50), 44, H - 38);
    if (logo.complete && logo.naturalWidth) ctx.drawImage(logo, 24, 18, 150, 100);
  };
  const timer = setInterval(draw, 1000 / 24);
  const ac = new AudioContext(), dest = ac.createMediaStreamDestination(), wired = new Set();
  const wire = () => getStreams().forEach(s => { if (s && !wired.has(s.id) && s.getAudioTracks().length) { wired.add(s.id); ac.createMediaStreamSource(s).connect(dest); } });
  wire();
  const wireTimer = setInterval(wire, 1500);
  const rec = new MediaRecorder(new MediaStream([...canvas.captureStream(24).getVideoTracks(), ...dest.stream.getAudioTracks()]), mime ? { mimeType: mime } : undefined);
  rec.ondataavailable = (e) => e.data.size && onData(e.data);
  rec.start(1000);
  return { mime: mime || "video/webm", stop: () => new Promise(res => { rec.onstop = () => { clearInterval(timer); clearInterval(wireTimer); ac.close(); res(); }; rec.stop(); }) };
}
